import { useEffect, useState } from 'react';
import { Icon } from '../components/Icon';
import { api, ApiError } from '../lib/api';

// Exception ledger → Non-clients (Bettina, 2026-10-29): the keywords that flag
// a client name as "possibly not a real client", and every client a reviewer
// has marked "Not a real client". Admins edit the keywords and can mark a
// listed client as a client again, which sends its case back to review.

interface Keyword { id: number; word: string; created_by: string | null; created_at: string }
interface RealClient {
  id: number; site_number: number; axiscare_client_id: string; origin_case_id: number | null;
  client_name: string | null; location: string | null; note: string | null;
  confirmed_by: string | null; confirmed_at: string; keyword_hits: string[];
}
interface NonClient {
  id: number;
  axiscare_client_id: string;
  site_number: number | null;
  franchisee_af_stem: string;
  origin_case_id: number | null;
  client_name: string | null;
  location: string | null;
  note: string | null;
  marked_by: string | null;
  marked_at: string;
  keyword_hits: string[];
}

const when = (v: string | null) => (v ? new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const errText = (e: unknown, fallback: string) => {
  const body = e instanceof ApiError ? (e.body as { error?: string } | null) : null;
  return body?.error ?? (e instanceof Error ? e.message : fallback);
};

export function NonClients({ role }: { role: 'admin' | 'reviewer' }) {
  const [keywords, setKeywords] = useState<Keyword[] | null>(null);
  const [rows, setRows] = useState<NonClient[] | null>(null);
  const [awaiting, setAwaiting] = useState(0);
  const [real, setReal] = useState<RealClient[]>([]);
  const [newWord, setNewWord] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const isAdmin = role === 'admin';

  const load = () => api<{ keywords: Keyword[]; non_clients: NonClient[]; awaiting_review: number; confirmed_real: RealClient[] }>('/api/non-clients')
    .then((d) => { setKeywords(d.keywords); setRows(d.non_clients); setAwaiting(d.awaiting_review); setReal(d.confirmed_real ?? []); })
    .catch((e) => setError(errText(e, 'Failed to load')));
  useEffect(() => { load(); }, []);

  const addWord = async () => {
    if (!newWord.trim()) return;
    setBusy('add'); setError(null); setNotice(null);
    try {
      await api('/api/non-clients/keywords', { method: 'POST', body: { word: newWord } });
      setNotice(`Added "${newWord.trim().toLowerCase()}". It applies from the next audit run.`);
      setNewWord('');
      load();
    } catch (e) { setError(errText(e, 'Could not add the keyword')); } finally { setBusy(null); }
  };

  const removeWord = async (k: Keyword) => {
    if (!confirm(`Remove "${k.word}"?\n\nFrom the next audit run, client names containing it are no longer flagged as possibly not a real client. Clients already marked "Not a real client" stay marked.`)) return;
    setBusy(`kw-${k.id}`); setError(null); setNotice(null);
    try {
      await api(`/api/non-clients/keywords/${k.id}`, { method: 'DELETE' });
      setNotice(`Removed "${k.word}". It stops applying from the next audit run.`);
      load();
    } catch (e) { setError(errText(e, 'Could not remove the keyword')); } finally { setBusy(null); }
  };

  const markClient = async (r: NonClient) => {
    const note = prompt(
      `Mark ${r.client_name ?? r.axiscare_client_id} (${r.franchisee_af_stem}) as a client?\n\n`
      + 'This lifts the "Not a real client" suppression and moves its closed case back to NEW for review. '
      + 'Future audits evaluate this client normally again.\n\nOptional note:',
      '',
    );
    if (note === null) return;
    setBusy(`nc-${r.id}`); setError(null); setNotice(null);
    try {
      const d = await api<{ reopened: number[] }>(`/api/non-clients/${r.id}/mark-client`, { method: 'POST', body: { note: note.trim() || undefined } });
      setNotice(d.reopened.length
        ? `Marked as a client. Back to review: ${d.reopened.map((id) => `case #${id}`).join(', ')}.`
        : 'Marked as a client. No closed case was found to reopen — the next audit covering this franchisee will evaluate it.');
      load();
    } catch (e) { setError(errText(e, 'Could not mark as a client')); } finally { setBusy(null); }
  };

  const undoReal = async (r: RealClient) => {
    if (!confirm(`Undo the "real client" ruling for ${r.client_name ?? r.axiscare_client_id}?\n\nFuture audits will flag this client as a possible non-client again if the name contains a keyword. Current cases are not changed.`)) return;
    setBusy(`real-${r.id}`); setError(null); setNotice(null);
    try {
      await api(`/api/non-clients/confirmed/${r.id}/undo`, { method: 'POST' });
      setNotice('Ruling undone. The flag returns on the next audit that covers this client.');
      load();
    } catch (e) { setError(errText(e, 'Could not undo')); } finally { setBusy(null); }
  };

  const needle = q.trim().toLowerCase();
  const shown = (rows ?? []).filter((r) => !needle
    || (r.client_name ?? '').toLowerCase().includes(needle)
    || r.franchisee_af_stem.toLowerCase().includes(needle)
    || r.axiscare_client_id.toLowerCase().includes(needle));

  return (
    <div>
      <div style={{ fontSize: 13, color: 'var(--text-muted)', maxWidth: 820, marginBottom: 12 }}>
        Office, training, staff and caregiver records that AxisCare carries as clients. When a client name contains one of the
        keywords below as a whole word, the audit sends that case to review flagged <strong>Possibly not a real client</strong>.
        A reviewer then marks it <strong>Not a real client</strong>, which closes it and stops future audits alerting on that
        client for that franchisee (every zip).
      </div>

      {notice && <div className="card card-pad" style={{ fontSize: 12.5, marginBottom: 12, background: 'var(--info-soft)' }}>{notice}</div>}
      {error && <div className="card card-pad c-warn" style={{ fontSize: 13, marginBottom: 12 }}>{error}</div>}

      <div className="card card-pad" style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, marginBottom: 10 }}>Keywords</div>
        {!keywords && <div className="muted" style={{ fontSize: 13 }}>Loading…</div>}
        {keywords && keywords.length === 0 && (
          <div className="c-warn" style={{ fontSize: 12.5, marginBottom: 8 }}>No keywords — audits will not flag any client as possibly not a real client.</div>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {keywords?.map((k) => (
            <span key={k.id} className="mono" title={k.created_by ? `Added by ${k.created_by}, ${when(k.created_at)}` : 'Original list'}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', border: '1px solid var(--border)', borderRadius: 12, fontSize: 12.5 }}>
              {k.word}
              {isAdmin && (
                <button className="btn btn-ghost btn-sm" style={{ padding: 0, minHeight: 0, lineHeight: 1 }} disabled={busy != null}
                        onClick={() => removeWord(k)} title={`Remove "${k.word}"`}>
                  <Icon name="close" size={11} />
                </button>
              )}
            </span>
          ))}
        </div>
        {isAdmin && (
          <div style={{ display: 'flex', gap: 6, marginTop: 10, alignItems: 'center' }}>
            <input className="input" value={newWord} onChange={(e) => setNewWord(e.target.value)} placeholder="Add a keyword (one word)"
                   onKeyDown={(e) => { if (e.key === 'Enter') addWord(); }} style={{ width: 240 }} />
            <button className="btn btn-sm" onClick={addWord} disabled={busy != null || !newWord.trim()}><Icon name="plus" size={12} /> Add</button>
            <span className="muted" style={{ fontSize: 11.5 }}>Whole words only: "cg" matches "CG Smith", not "McGee". Changes apply from the next audit run.</span>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Marked not a real client {rows && <span className="muted" style={{ fontWeight: 400 }}>({rows.length})</span>}</h2>
          {awaiting > 0 && (
            <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
              {awaiting} open case{awaiting === 1 ? '' : 's'} flagged <strong>Possibly not a real client</strong> still waiting for a reviewer in Cases.
            </div>
          )}
        </div>
        <input className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search client, AF# or client id" style={{ width: 260 }} />
      </div>

      {!rows && !error && <div className="card card-pad muted" style={{ fontSize: 13 }}>Loading…</div>}
      {rows && rows.length === 0 && <div className="card card-pad muted" style={{ fontSize: 13 }}>No clients have been marked "Not a real client" yet.</div>}
      {rows && rows.length > 0 && (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table className="tbl">
            <thead><tr>
              <th>Client</th><th>Servicing AF</th><th>Site</th><th>Keywords</th><th>Case</th><th>Marked</th><th>Note</th>{isAdmin && <th />}
            </tr></thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.id}>
                  <td>
                    <div>{r.client_name ?? <span className="c-warn">Not in the client list</span>}</div>
                    <div className="muted mono" style={{ fontSize: 11.5 }}>{r.axiscare_client_id}{r.location ? ` · ${r.location}` : ''}</div>
                  </td>
                  <td className="mono">{r.franchisee_af_stem}</td>
                  <td className="mono">{r.site_number ?? '—'}</td>
                  <td className="mono" style={{ fontSize: 12 }}>{r.keyword_hits.length ? r.keyword_hits.join(', ') : <span className="muted">none (marked by hand)</span>}</td>
                  <td>{r.origin_case_id
                    ? <a href={`${window.location.origin}/?case=${r.origin_case_id}`} target="_blank" rel="noreferrer" className="mono">#{r.origin_case_id}</a>
                    : '—'}</td>
                  <td style={{ fontSize: 12 }}>{when(r.marked_at)}{r.marked_by && <div className="muted">{r.marked_by}</div>}</td>
                  <td style={{ fontSize: 12, maxWidth: 260 }}>{r.note ?? <span className="muted">—</span>}</td>
                  {isAdmin && (
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn btn-sm" disabled={busy != null} onClick={() => markClient(r)} title="Lift the suppression and send the case back to review">
                        <Icon name="refresh" size={12} /> {busy === `nc-${r.id}` ? 'Saving…' : 'Mark as client'}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {shown.length === 0 && <tr><td colSpan={isAdmin ? 8 : 7} className="muted">No match for “{q}”.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <h2 style={{ margin: '28px 0 4px', fontSize: 16, fontWeight: 600 }}>Confirmed real clients <span className="muted" style={{ fontWeight: 400 }}>({real.length})</span></h2>
      <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
        Clients whose name contains a keyword but a reviewer confirmed are real ("Real client" on the case, or "Mark as client" above). Audits no longer flag them as possible non-clients.
      </div>
      {real.length === 0 ? <div className="card card-pad muted" style={{ fontSize: 13 }}>None yet.</div> : (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table className="tbl">
            <thead><tr><th>Client</th><th>Site</th><th>Keywords</th><th>Case</th><th>Confirmed</th><th>Note</th>{isAdmin && <th />}</tr></thead>
            <tbody>
              {real.map((r) => (
                <tr key={r.id}>
                  <td>
                    <div>{r.client_name ?? <span className="c-warn">Not in the client list</span>}</div>
                    <div className="muted mono" style={{ fontSize: 11.5 }}>{r.axiscare_client_id}{r.location ? ` · ${r.location}` : ''}</div>
                  </td>
                  <td className="mono">{r.site_number}</td>
                  <td className="mono" style={{ fontSize: 12 }}>{r.keyword_hits.length ? r.keyword_hits.join(', ') : <span className="muted">none now</span>}</td>
                  <td>{r.origin_case_id ? <a href={`${window.location.origin}/?case=${r.origin_case_id}`} target="_blank" rel="noreferrer" className="mono">#{r.origin_case_id}</a> : '—'}</td>
                  <td style={{ fontSize: 12 }}>{when(r.confirmed_at)}{r.confirmed_by && <div className="muted">{r.confirmed_by}</div>}</td>
                  <td style={{ fontSize: 12, maxWidth: 260 }}>{r.note ?? <span className="muted">—</span>}</td>
                  {isAdmin && <td><button className="btn btn-sm btn-ghost" disabled={busy != null} onClick={() => undoReal(r)}>Undo</button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
